import React, { FC, useState } from 'react'
import styled, { css } from 'styled-components'
import { useDebouncedCallback } from 'use-debounce'

import { OtherService } from '@ads/front-core/api'
import { breakpoints } from '@ads/front-core/constants/breakpoints'
import { useAdsForm } from '@ads/front-core/hooks/useAdsForm'
import TextInput from '@ads/front-core/components/Form/TextInput'
import { joiSchema, IForm } from './formConfig'
import { lng } from '@ads/front-core/locales'

export const Form: FC = () => {
  const [isSent, setIsSent] = useState(false)

  const setIsSentDebounced = useDebouncedCallback(setIsSent, 3000)

  const {
    control,
    formState: { errors },
    isSubmitting,
    handleSubmit,
  } = useAdsForm<IForm>({
    joiSchema,
    onSubmit: async data => {
      await OtherService.sendContact({
        requestBody: {
          email: data.email,
          name: data.name,
          phone: data.phone,
          message: '',
          subject: lng('lppartners:contact.subject'),
          to: 'PARTNERS',
          source: 'LP_PARTNERS',
        },
      })

      setIsSent(true)
      setIsSentDebounced(false)
    },
  })

  return (
    <OuterWrapper>
      <Wrapper>
        <FormContent id="pform">
          <FormText>{lng('lppartners:form.header')}</FormText>
          <FormCaption>{lng('lppartners:form.text')}</FormCaption>
          <TextInputStyled
            control={control}
            name="name"
            error={errors?.name}
            placeholder={lng('common:firstName')}
            label={lng('common:firstName')}
          />
          <TextInputStyled
            control={control}
            name="email"
            error={errors?.email}
            placeholder={lng('common:emailAddress')}
            label="E-mail"
          />
          <TextInputStyled
            control={control}
            name="phone"
            error={errors?.phone}
            placeholder={lng('landing:phone')}
            label={lng('landing:phone')}
          />
          <SendButton
            onClick={handleSubmit}
            isExpanded={isSent}
            disabled={isSubmitting}
          >
            <span>{lng(isSent ? 'common:messageWasSent' : 'common:send')}</span>
          </SendButton>
        </FormContent>
      </Wrapper>
    </OuterWrapper>
  )
}

const OuterWrapper = styled.div`
  background-color: #262a44;
  position: relative;
`

const Wrapper = styled.div`
  font-family: Poppins;
  max-width: 1348px;
  margin: 0 auto;
  padding: 60px;
  color: #fff;
  position: relative;
  z-index: 1;
  background: url(/img/partners/3.jpg) no-repeat right center;
  background-size: auto 100%;

  @media (max-width: ${breakpoints.MOBILE}) {
    padding: 24px 16px 405px 16px;
    background: url(/img/partners/3-mob.jpg) no-repeat bottom center;
    background-size: 100% auto;
  }
`

const FormContent = styled.div`
  max-width: 674px;
  display: flex;
  flex-flow: column;
  gap: 24px;
`

const FormText = styled.p`
  font-size: 36px;
  font-weight: 600;

  @media (max-width: ${breakpoints.MOBILE}) {
    font-size: 26px;
  }
`

const FormCaption = styled.p`
  font-size: 18px;
  font-weight: 200;
`

const SendButton = styled.button<{ disabled?: boolean; isExpanded: boolean }>`
  ${p => css`
    display: flex;
    align-self: flex-end;
    justify-content: flex-start;

    border: 0px solid transparent;
    color: #fff;
    background-color: #ec1c24;

    font-size: 18px;
    border-radius: 50px;
    height: 55px;

    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    text-decoration: none;
    padding: 14px 36px;
    white-space: nowrap;

    &:hover {
      background-color: #ec1c24;
      color: white;
    }

    cursor: ${p.disabled ? 'not-allowed' : 'pointer'};
    ${p.disabled &&
    css`
      opacity: 0.8;
    `}

    position: relative;
    width: ${p.isExpanded ? 340 : 130}px;
    overflow: hidden;
    transition: 0.5s width, 0.3s opacity;

    &:after {
      position: absolute;
      background: linear-gradient(
        90deg,
        rgba(236, 28, 36, 0) 0%,
        rgb(236, 28, 36) 50%
      );
      content: '';
      right: 0px;
      width: 20px;
      top: 0;
      bottom: 0;
    }

    span {
      text-align: center;
      width: 100%;
    }
  `}

  @media (max-width: ${breakpoints.MOBILE}) {
    width: 100%;
  }
`

const TextInputStyled = styled(TextInput)`
  input {
    height: 55px;
    padding: 14px 36px;
    border: 2px solid #e6e8ed;
    border-radius: 50px;
    font-size: 18px;
  }
  label {
    font-family: Poppins, sans-serif;
    color: #fff;
    font-size: 18px;
    font-weight: 200;
    padding-left: 10px;
  }
`
